'use client'

import { useEffect, useRef } from 'react'
import { type ClientJourney, startClientJourney } from './metrics-browser'

/**
 * Measures committed subscription -> first result, including an immediately
 * available result. A result is data availability, not painted/usable content.
 * identity stays local: query arguments must never become tags or RUM context.
 */
export function useQueryTelemetry(options: {
  enabled: boolean
  identity: string
  functionName: string
  ready: boolean
  failed?: boolean
  kind: 'query' | 'queries' | 'soft_query' | 'paginated_query'
}): void {
  const { enabled, identity, functionName, ready, failed, kind } = options
  const active = useRef<{
    handle: ClientJourney
    identity: string
    functionName: string
    settled: boolean
  } | null>(null)
  useEffect(() => {
    if (!enabled) {
      active.current?.handle.finish('cancelled', {
        tags: { reason: 'subscription_closed' },
      })
      active.current = null
      return
    }
    // The cached query already canonicalizes arguments. Do not serialize them
    // again for telemetry: a new loading interval is the observable boundary.
    if (
      !active.current ||
      active.current.identity !== identity ||
      active.current.functionName !== functionName ||
      (!ready && !failed && active.current.settled)
    ) {
      active.current?.handle.finish('cancelled', {
        tags: { reason: 'subscription_changed' },
      })
      active.current = {
        identity,
        functionName,
        settled: false,
        handle: startClientJourney('client.query.first_data', {
          tags: {
            domain: 'data',
            surface: kind,
            function: functionName,
            transport: 'convex_subscription',
            cache_state: ready ? 'immediate_result' : 'waiting',
          },
        }),
      }
    }
    if (failed || ready) {
      active.current.handle.finish(failed ? 'failure' : 'success')
      active.current.settled = true
    }
  }, [enabled, identity, functionName, kind, ready, failed])
  useEffect(
    () => () => {
      active.current?.handle.finish('cancelled', {
        tags: { reason: 'unmounted' },
      })
      active.current = null
    },
    []
  )
}
